import mapper from 'app/libraries/utils/auto-mapper';
import { Folder, Message, Post, SpotPolicy, User } from '../types';
import { Response as RawFolder } from '../../api/posts-read';
import { upgradeDeprecated } from './upgrade-deprecate';
import { decodeText } from '../../../../../../utils/stringUtils';

type ParsedFolder = {
  spotPolicy: SpotPolicy;
  messages: Message[];
  posts: Post[];
  users: User[];
  locked: boolean;
  expirationTime: number;
  count: number;
};

export function parseFolder(folderName: Folder, data: RawFolder): ParsedFolder {
  const folders = data.folders;

  if (!folders.hasOwnProperty(folderName)) {
    throw new Error(`parseFolder: data has no folder with name ${folderName}`);
  }

  const folder = folders[folderName];

  const mappedMessages = messagesMapper(folder.messages);

  mappedMessages?.forEach(message => upgradeDeprecated(message));

  const mappedUsers = Object.keys(folder.users || {}).map((userId: string) => {
    const mappedUser = userMapper(folder.users[userId]);
    mappedUser.username = decodeText(mappedUser.username);
    mappedUser.displayName = decodeText(mappedUser.displayName);

    return {
      ...mappedUser,
      id: userId
    };
  });

  const mappedPosts = Object.keys(folder.posts_metadata || {}).map((postId: string) => ({
    ...mapper.map(folder.posts_metadata[postId], {
      title: 'title',
      url: 'postUrl',
      policy: 'policy'
    }),
    id: postId
  }));

  return {
    spotPolicy: folder?.spot_policy,
    expirationTime: data.expiration_time,
    locked: data.should_lock,
    messages: mappedMessages,
    users: mappedUsers,
    count: folder.count,
    posts: mappedPosts
  };
}

export function userMapper(data) {
  return mapper.map(data, {
    id: 'id',
    email: 'email',
    display_name: 'displayName',
    registered: 'isRegistered',
    image_id: 'imageId',
    fake_image: 'isDefaultImage',
    points: 'points',
    user_name: 'username',
    online: 'isOnline',
    whitelisted: 'isWhitelisted',
    blacklisted: 'isBlacklisted',
    roles: 'roles'
  });
}

export function messagesMapper(data) {
  return mapper.map(data, {
    '~': 'messages',
    id: 'id',
    conv_message_id: 'conversationMessageId',
    conversation_id: 'conversationId',
    type: 'type',
    state: 'status',
    published: 'published',
    tags: 'tags',
    ip: 'ip',
    tags_metadata: 'tagsData',

    author_id: 'userId',
    timestamp: 'postedAt',

    parent_comment_id: 'parentId',
    conversation_url: 'messageUrl',
    conversation_title: 'conversationTitle',
    user_id: 'userId',
    reports_count: 'reportsCount',
    manually_resolved: 'isManuallyResolved',
    edited: 'isEdited',
    anonymous: 'isAnonymous',
    in_processing: 'isProcessing',

    'metadata.display_name': 'userDisplayName',
    'metadata.highlight.parsed_content': 'highlightedWords',
    'metadata.hashtags': 'hashtags',
    safety_score: 'safetyScore',
    content: {
      '~': 'entities',
      type: 'type',
      frameType: 'frameType',
      id: 'id',
      text: 'text',
      imageId: 'imageId',
      originalHeight: 'originalHeight',
      originalWidth: 'originalWidth',
      mediaHeight: 'mediaHeight',
      mediaWidth: 'mediaWidth',
      thumbnailHeight: 'thumbnailHeight',
      thumbnailWidth: 'thumbnailWidth',
      title: 'title',
      description: 'description',
      url: 'url',
      originalUrl: 'originalUrl',
      userId: 'userId',
      username: 'username',
      embed: 'embed'
    }
  });
}
